import React, { useState, useEffect, useMemo } from 'react';
import { Car, Wrench, Phone, Mail, MessageCircle, MapPin, CalendarCheck, ChevronLeft, ChevronRight, Gauge, Calendar, Loader2, Tag, Search, X, ArrowUpDown, Share2, Check, Globe, UserCircle } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const waNum = (s) => (s || '').replace(/[^0-9]/g, '').replace(/^0/, '31');

const makeColors = (accent, theme) => theme === 'light'
  ? { accent, bg: '#f1f5f9', headBg: '#ffffff', surface: '#ffffff', surface2: '#f8fafc', border: '#e2e8f0', border2: '#cbd5e1', text: '#0f172a', textSoft: '#334155', textMuted: '#64748b', overlay: 'rgba(15,23,42,.55)' }
  : { accent, bg: '#0f172a', headBg: 'linear-gradient(135deg,#1e293b,#0f172a)', surface: '#1e293b', surface2: '#0f172a', border: '#1e293b', border2: '#334155', text: '#ffffff', textSoft: '#cbd5e1', textMuted: '#94a3b8', overlay: 'rgba(0,0,0,.7)' };

const SORTS = {
  occasions: [
    { id: 'nieuwste', label: 'Nieuwste eerst' },
    { id: 'prijs-op', label: 'Prijs laag → hoog' },
    { id: 'prijs-af', label: 'Prijs hoog → laag' },
    { id: 'bouwjaar', label: 'Bouwjaar (nieuw → oud)' },
    { id: 'km', label: 'Km-stand (laag → hoog)' },
  ],
  onderdelen: [
    { id: 'nieuwste', label: 'Nieuwste eerst' },
    { id: 'prijs-op', label: 'Prijs laag → hoog' },
    { id: 'prijs-af', label: 'Prijs hoog → laag' },
  ],
};

const sortList = (list, key) => {
  const a = [...list];
  const num = (x) => Number(x) || 0;
  switch (key) {
    case 'prijs-op': return a.sort((x, y) => num(x.prijs) - num(y.prijs));
    case 'prijs-af': return a.sort((x, y) => num(y.prijs) - num(x.prijs));
    case 'bouwjaar': return a.sort((x, y) => num(y.bouwjaar) - num(x.bouwjaar));
    case 'km': return a.sort((x, y) => num(x.kmStand) - num(y.kmStand));
    case 'nieuwste': default: return a.sort((x, y) => String(y.updatedAt || y.createdAt || '').localeCompare(String(x.updatedAt || x.createdAt || '')));
  }
};

export default function ShowroomPublic({ slug }) {
  const { t, lang, setLang, languages, autoDetected, dismissAutoDetected } = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('occasions');
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [merkF, setMerkF] = useState('');
  const [sort, setSort] = useState('nieuwste');
  const [pageSize, setPageSize] = useState(16);
  const [page, setPage] = useState(1);

  useEffect(() => { setQ(''); setCat(''); setMerkF(''); setSort('nieuwste'); setPage(1); }, [tab]);
  useEffect(() => { setPage(1); }, [q, cat, merkF, sort, pageSize]);

  useEffect(() => {
    carsysApi.getPublicShowroom(slug).then(setData).catch(e => setError(e.response?.status === 404 ? 'notfound' : 'error'));
  }, [slug]);

  const garage = data?.garage;
  const c = useMemo(() => makeColors(garage?.accent || '#7c3aed', garage?.theme || 'dark'), [garage]);

  const itemMerken = (item) => {
    const set = new Set();
    if (item.merk) set.add(item.merk);
    if (item.afkomstigVoertuig) set.add(String(item.afkomstigVoertuig).split(' ')[0]);
    (item.pastOp || []).forEach(p => p.merk && set.add(p.merk));
    return [...set];
  };
  const searchText = (item) => [item.titel, item.naam, item.categorie, item.merk, item.model, item.afkomstigVoertuig, item.motor, item.oemNummer, item.onderdeelNummer,
    ...(item.pastOp || []).map(p => `${p.merk} ${p.model} ${p.bouwjaar}`), ...(item.oemCrossRef || [])].filter(Boolean).join(' ').toLowerCase();

  if (error === 'notfound') return <Center>🔍 {t('Showroom niet gevonden.')}</Center>;
  if (error) return <Center>{t('Er ging iets mis. Probeer het later opnieuw.')}</Center>;
  if (!data) return <Center><Loader2 className="spin" size={28} /></Center>;

  const { occasions, onderdelen } = data;
  const openDetail = async (item) => {
    setDetail(item);
    try { const full = await carsysApi.getPublicShowroomListing(slug, item.id); setDetail(full); } catch { /* houd basis */ }
  };

  const applyFilter = (list) => list.filter(item => {
    if (q.trim() && !searchText(item).includes(q.trim().toLowerCase())) return false;
    if (cat && (item.categorie || '') !== cat) return false;
    if (merkF && !itemMerken(item).some(m => m.toLowerCase() === merkF.toLowerCase())) return false;
    return true;
  });
  const baseList = tab === 'occasions' ? occasions : onderdelen;
  const filtered = sortList(applyFilter(baseList), sort);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const catOptions = [...new Set(onderdelen.map(o => o.categorie).filter(Boolean))].sort();
  const merkOptions = [...new Set(baseList.flatMap(itemMerken).filter(Boolean))].sort();

  return (
    <div style={{ minHeight: '100vh', background: c.bg, color: c.textSoft, fontFamily: 'system-ui,sans-serif' }} data-testid="showroom-public">
      {garage.heeftBanner && (
        <div style={{ width: '100%', height: 220, background: `${c.surface2} center/cover no-repeat`, backgroundImage: `url(${carsysApi.showroomBannerUrl(slug)})` }} data-testid="showroom-banner" />
      )}
      <header style={{ background: c.headBg, borderBottom: `1px solid ${c.border}`, padding: '24px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {garage.heeftLogo && <img src={carsysApi.showroomLogoUrl(slug)} alt="logo" style={{ height: 52, borderRadius: 8, background: '#fff', padding: 4 }} onError={e => { e.target.style.display = 'none'; }} />}
          <div style={{ flex: 1, minWidth: 160 }}>
            <h1 style={{ margin: 0, fontSize: 26, color: c.text }} data-testid="showroom-naam">{garage.naam}</h1>
            {(garage.plaats || garage.adres) && <div style={{ fontSize: 13.5, color: c.textMuted, display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}><MapPin size={14} /> {[garage.adres, garage.plaats].filter(Boolean).join(', ')}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <ShareButton c={c} garage={garage} t={t} />
            {languages.length > 1 && (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <select value={lang} onChange={e => setLang(e.target.value)} data-testid="showroom-lang-select"
                  style={{ background: c.surface, border: `1px solid ${autoDetected ? c.accent : c.border2}`, borderRadius: 8, color: c.text, padding: '8px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', outline: 'none' }}>
                  {languages.map(l => <option key={l.code} value={l.code}>{l.flag ? `${l.flag} ` : ''}{(l.code || '').toUpperCase()}</option>)}
                </select>
                {autoDetected && (
                  <div data-testid="lang-autodetect-hint"
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 30, display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', background: c.accent, color: '#fff', padding: '7px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, boxShadow: '0 6px 20px rgba(0,0,0,.25)' }}>
                    <Globe size={14} />
                    <span>{t('We tonen deze site in jouw taal — wijzig hier')}</span>
                    <button onClick={dismissAutoDetected} data-testid="lang-autodetect-dismiss" aria-label={t('Sluiten')}
                      style={{ background: 'rgba(255,255,255,.25)', border: 'none', borderRadius: 5, color: '#fff', cursor: 'pointer', display: 'flex', padding: 2 }}>
                      <X size={13} />
                    </button>
                  </div>
                )}
              </div>
            )}
            {garage.telefoon && <a href={`tel:${garage.telefoon}`} style={hdrBtn(c.border2, c.text)}><Phone size={15} /> {garage.telefoon}</a>}
            {garage.whatsapp && <a href={`https://wa.me/${waNum(garage.whatsapp)}`} target="_blank" rel="noreferrer" style={hdrBtn('#16a34a', '#fff')}><MessageCircle size={15} /> WhatsApp</a>}
            {garage.portaalLink && <a href={garage.portaalLink} target="_blank" rel="noreferrer" data-testid="showroom-portaal-link" style={hdrBtn(c.accent, '#fff')}><UserCircle size={15} /> {t('Klantportaal', 'Customer portal')}</a>}
          </div>
        </div>
      </header>

      <nav style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px', display: 'flex', gap: 6, borderBottom: `1px solid ${c.border}` }}>
        {[['occasions', `${t('Occasions')} (${occasions.length})`], ['onderdelen', `${t('Onderdelen')} (${onderdelen.length})`], ['contact', t('Contact')], ['over', t('Over ons')]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`showroom-tab-${id}`}
            style={{ padding: '14px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: tab === id ? c.text : c.textMuted, borderBottom: tab === id ? `2px solid ${c.accent}` : '2px solid transparent' }}>{lbl}</button>
        ))}
      </nav>

      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px 60px' }}>
        {(tab === 'occasions' || tab === 'onderdelen') && (
          <>
            <FilterBar c={c} tab={tab} q={q} setQ={setQ} merkF={merkF} setMerkF={setMerkF} merkOptions={merkOptions}
              sort={sort} setSort={setSort} pageSize={pageSize} setPageSize={setPageSize}
              total={baseList.length} shown={filtered.length} onClear={() => { setQ(''); setCat(''); setMerkF(''); }} heeftFilter={!!(q || cat || merkF)} />
            {tab === 'onderdelen' && catOptions.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }} data-testid="showroom-cat-tabs">
                <CatTab c={c} active={cat === ''} onClick={() => setCat('')}>{t('Alle')}</CatTab>
                {catOptions.map(co => <CatTab key={co} c={c} active={cat === co} onClick={() => setCat(co)} testid={`showroom-cat-${co}`}>{co}</CatTab>)}
              </div>
            )}
            {paged.length === 0 ? <Empty c={c}>{baseList.length === 0 ? `${t('Nog geen')} ${tab} ${t('beschikbaar.')}` : t('Geen resultaten voor deze filters.')}</Empty> : (
              <div style={grid} data-testid={`showroom-${tab}`}>
                {paged.map(o => <Kaart key={o.id} c={c} item={o} onClick={() => openDetail(o)} />)}
              </div>
            )}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 28 }} data-testid="showroom-pagination">
                <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="showroom-page-prev" style={pgBtn(c, safePage <= 1)}><ChevronLeft size={16} /> {t('Vorige')}</button>
                <span style={{ color: c.textMuted, fontSize: 13.5 }}>{t('Pagina')} {safePage} / {totalPages}</span>
                <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="showroom-page-next" style={pgBtn(c, safePage >= totalPages)}>{t('Volgende')} <ChevronRight size={16} /></button>
              </div>
            )}
          </>
        )}
        {tab === 'contact' && (
          <div data-testid="showroom-contact" style={{ maxWidth: 480 }}>
            <h2 style={{ color: c.text }}>{t('Contact')}</h2>
            <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
              {garage.telefoon && <a href={`tel:${garage.telefoon}`} style={contactRow(c)}><Phone size={18} color={c.accent} /> {garage.telefoon}</a>}
              {garage.whatsapp && <a href={`https://wa.me/${waNum(garage.whatsapp)}`} target="_blank" rel="noreferrer" style={contactRow(c)}><MessageCircle size={18} color="#16a34a" /> {t('WhatsApp ons')}</a>}
              {garage.email && <a href={`mailto:${garage.email}`} style={contactRow(c)}><Mail size={18} color="#2563eb" /> {garage.email}</a>}
              {(garage.adres || garage.plaats) && <div style={contactRow(c)}><MapPin size={18} color="#ea580c" /> {[garage.adres, garage.plaats].filter(Boolean).join(', ')}</div>}
              {garage.portaalLink && <a href={garage.portaalLink} target="_blank" rel="noreferrer" data-testid="showroom-contact-portaal" style={contactRow(c)}><UserCircle size={18} color={c.accent} /> {t('Klantportaal — bekijk je facturen & afspraken', 'Customer portal — view your invoices & appointments')}</a>}
            </div>
          </div>
        )}
        {tab === 'over' && (
          <div data-testid="showroom-over" style={{ maxWidth: 640 }}>
            <h2 style={{ color: c.text }}>{t('Over ons')} {garage.naam}</h2>
            <p style={{ color: c.textSoft, lineHeight: 1.7, fontSize: 15 }}>{garage.overOns || `${t('Welkom bij')} ${garage.naam}. ${t('Bekijk onze occasions en onderdelen en neem gerust contact op.')}`}</p>
          </div>
        )}
      </main>

      {detail && <DetailModal c={c} slug={slug} item={detail} garage={garage} onClose={() => setDetail(null)} />}
      <footer style={{ textAlign: 'center', padding: 20, color: c.textMuted, fontSize: 12, borderTop: `1px solid ${c.border}` }}>Powered by MonteurCentraal</footer>
      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ShareButton({ c, garage, t }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = `${garage?.naam || ''} — ${t('Bekijk onze occasions en onderdelen')}`;
  const doNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: garage?.naam, text: shareText, url }); } catch (_e) { /* cancelled */ } }
    else setOpen(o => !o);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (_e) { /* noop */ }
  };
  return (
    <div style={{ position: 'relative' }}>
      <button onClick={doNative} data-testid="showroom-share-btn" style={hdrBtn(c.border2, c.text)}>
        {copied ? <Check size={15} /> : <Share2 size={15} />} {copied ? t('Gekopieerd!') : t('Deel')}
      </button>
      {open && (
        <div data-testid="showroom-share-menu" style={{ position: 'absolute', top: '110%', right: 0, background: c.surface, border: `1px solid ${c.border2}`, borderRadius: 10, padding: 6, display: 'grid', gap: 4, zIndex: 60, minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,.25)' }}>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + url)}`} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} data-testid="showroom-share-whatsapp"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', borderRadius: 7, color: c.textSoft, textDecoration: 'none', fontSize: 13.5 }}><MessageCircle size={15} color="#16a34a" /> WhatsApp</a>
          <button onClick={() => { copy(); setOpen(false); }} data-testid="showroom-share-copy"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 11px', borderRadius: 7, color: c.textSoft, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, textAlign: 'left' }}><Share2 size={15} /> {t('Kopieer link')}</button>
        </div>
      )}
    </div>
  );
}

function CatTab({ c, active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ padding: '7px 14px', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: `1px solid ${active ? c.accent : c.border2}`, background: active ? c.accent : c.surface, color: active ? '#fff' : c.textSoft }}>{children}</button>
  );
}

function FilterBar({ c, tab, q, setQ, merkF, setMerkF, merkOptions, sort, setSort, pageSize, setPageSize, total, shown, onClear, heeftFilter }) {
  const { t } = useT();
  const inputStyle = { background: c.surface, border: `1px solid ${c.border2}`, borderRadius: 8, color: c.text, padding: '9px 11px', fontSize: 14, outline: 'none' };
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }} data-testid="showroom-filterbar">
      <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180 }}>
        <Search size={16} style={{ position: 'absolute', left: 11, top: 11, color: c.textMuted }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={tab === 'onderdelen' ? t('Zoek op onderdeel, merk, model, OEM-nr...') : t('Zoek op merk, model...')}
          data-testid="showroom-search" style={{ ...inputStyle, width: '100%', paddingLeft: 34, boxSizing: 'border-box' }} />
      </div>
      {merkOptions.length > 0 && (
        <select value={merkF} onChange={e => setMerkF(e.target.value)} data-testid="showroom-filter-merk" style={inputStyle}>
          <option value="">{t('Alle merken')}</option>
          {merkOptions.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      )}
      <select value={sort} onChange={e => setSort(e.target.value)} data-testid="showroom-sort" style={inputStyle} title={t('Sorteren')}>
        {SORTS[tab].map(s => <option key={s.id} value={s.id}>{t(s.label)}</option>)}
      </select>
      <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} data-testid="showroom-pagesize" style={inputStyle} title={t('Per pagina')}>
        {[16, 32, 48].map(n => <option key={n} value={n}>{n} / {t('pagina')}</option>)}
      </select>
      {heeftFilter && (
        <button onClick={onClear} data-testid="showroom-filter-clear"
          style={{ ...inputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><X size={14} /> {t('Wis')}</button>
      )}
      <span style={{ color: c.textMuted, fontSize: 13, marginLeft: 'auto' }}>{shown} / {total}</span>
    </div>
  );
}

function Kaart({ c, item, onClick }) {
  const { t } = useT();
  const foto = (item.fotos || [])[0];
  const gereserveerd = item.status === 'Gereserveerd';
  return (
    <button onClick={onClick} data-testid={`showroom-card-${item.id}`} style={{ textAlign: 'left', border: `1px solid ${c.border}`, background: c.surface, borderRadius: 14, overflow: 'hidden', cursor: 'pointer', color: c.textSoft, padding: 0 }}>
      <div style={{ position: 'relative', height: 170, background: c.surface2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {foto ? <img src={foto} alt={item.titel} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (item.type === 'occasion' ? <Car size={50} color={c.textMuted} /> : <Wrench size={44} color={c.textMuted} />)}
        {gereserveerd && <span style={{ position: 'absolute', top: 10, left: 10, background: '#f59e0b', color: '#1a1a1a', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6 }}>{t('Gereserveerd')}</span>}
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: c.text, minHeight: 20 }}>{item.titel || item.naam}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: c.accent, margin: '6px 0' }}>{eur(item.prijs)}</div>
        <div style={{ fontSize: 12, color: c.textMuted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {item.type === 'occasion' ? (<>
            {item.bouwjaar && <span><Calendar size={12} style={{ verticalAlign: -1 }} /> {item.bouwjaar}</span>}
            {item.kmStand && <span><Gauge size={12} style={{ verticalAlign: -1 }} /> {Number(item.kmStand).toLocaleString('nl-NL')} km</span>}
          </>) : (<>
            <span><Tag size={12} style={{ verticalAlign: -1 }} /> {item.conditie}</span>
            {item.afkomstigVoertuig && <span>{item.afkomstigVoertuig}</span>}
          </>)}
        </div>
      </div>
    </button>
  );
}

function DetailModal({ c, slug, item, garage, onClose }) {
  const { t } = useT();
  const [foto, setFoto] = useState(0);
  const fotos = item.fotos || [];
  const klik = (kanaal) => { carsysApi.publicShowroomContact(slug, item.id, kanaal).catch(() => {}); };
  const tekst = encodeURIComponent(`${t('Hallo, ik heb interesse in:')} ${item.titel || item.naam} (${eur(item.prijs)})`);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: c.overlay, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px', zIndex: 50 }} data-testid="showroom-detail">
      <div onClick={e => e.stopPropagation()} style={{ background: c.bg, border: `1px solid ${c.border2}`, borderRadius: 16, maxWidth: 820, width: '96vw', color: c.textSoft }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: 16, borderBottom: `1px solid ${c.border}` }}>
          <button onClick={onClose} style={hdrBtn(c.border2, c.text)}><ChevronLeft size={16} /> {t('Terug')}</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,1fr)', gap: 20, padding: 20 }}>
          <div>
            <div style={{ height: 280, background: c.surface, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {fotos[foto] ? <img src={fotos[foto]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (item.type === 'occasion' ? <Car size={64} color={c.textMuted} /> : <Wrench size={56} color={c.textMuted} />)}
            </div>
            {fotos.length > 1 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                {fotos.map((f, i) => <img key={i} src={f} onClick={() => setFoto(i)} alt="" style={{ width: 56, height: 42, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', border: i === foto ? `2px solid ${c.accent}` : '2px solid transparent' }} />)}
              </div>
            )}
          </div>
          <div>
            <h2 style={{ margin: '0 0 4px', color: c.text, fontSize: 20 }}>{item.titel || item.naam}</h2>
            <div style={{ fontSize: 26, fontWeight: 800, color: c.accent, marginBottom: 12 }}>{eur(item.prijs)}</div>
            {item.type === 'occasion' ? (
              <div style={specGrid}>
                {item.kenteken && <Spec c={c} l={t('Kenteken')} v={item.kenteken} />}
                {item.bouwjaar && <Spec c={c} l={t('Bouwjaar')} v={item.bouwjaar} />}
                {item.kmStand && <Spec c={c} l={t('Km-stand')} v={`${Number(item.kmStand).toLocaleString('nl-NL')} km`} />}
                {item.brandstof && <Spec c={c} l={t('Brandstof')} v={item.brandstof} />}
                {item.kleur && <Spec c={c} l={t('Kleur')} v={item.kleur} />}
                {item.apk && <Spec c={c} l={t('APK')} v={item.apk} />}
              </div>
            ) : (
              <div style={specGrid}>
                {item.conditie && <Spec c={c} l={t('Conditie')} v={item.conditie} />}
                {item.aantal && <Spec c={c} l={t('Aantal')} v={item.aantal} />}
                {item.oemNummer && <Spec c={c} l={t('OEM')} v={item.oemNummer} />}
                {item.onderdeelNummer && <Spec c={c} l={t('Onderdeelnr')} v={item.onderdeelNummer} />}
                {item.afkomstigVoertuig && <Spec c={c} l={t('Voertuig')} v={item.afkomstigVoertuig} />}
                {item.bouwjaar && <Spec c={c} l={t('Bouwjaar')} v={item.bouwjaar} />}
                {item.kmStand && <Spec c={c} l={t('Km-stand')} v={`${Number(item.kmStand).toLocaleString('nl-NL')} km`} />}
                {item.motor && <Spec c={c} l={t('Motor')} v={item.motor} />}
              </div>
            )}
            {item.type !== 'occasion' && (item.pastOp || []).length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="detail-pastop">
                <div style={{ fontSize: 11, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('Past ook op')}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(item.pastOp || []).map((p, i) => (
                    <span key={i} style={{ background: c.surface, border: `1px solid ${c.border2}`, borderRadius: 999, padding: '4px 10px', fontSize: 12.5, color: c.textSoft }}>
                      {[p.merk, p.model, p.bouwjaar].filter(Boolean).join(' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {item.aiTekst && <p style={{ whiteSpace: 'pre-wrap', color: c.textSoft, fontSize: 13.5, lineHeight: 1.6, marginTop: 12 }}>{item.aiTekst}</p>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 16 }}>
              {garage.whatsapp && <a href={`https://wa.me/${waNum(garage.whatsapp)}?text=${tekst}`} target="_blank" rel="noreferrer" onClick={() => klik('whatsapp')} data-testid="detail-whatsapp" style={ctaBtn('#16a34a')}><MessageCircle size={16} /> WhatsApp</a>}
              {garage.telefoon && <a href={`tel:${garage.telefoon}`} onClick={() => klik('bel')} data-testid="detail-bel" style={ctaBtn(c.border2)}><Phone size={16} /> {t('Bellen')}</a>}
              {garage.email && <a href={`mailto:${garage.email}?subject=${tekst}`} onClick={() => klik('email')} data-testid="detail-email" style={ctaBtn('#2563eb')}><Mail size={16} /> {t('E-mail')}</a>}
              {item.type === 'occasion' && garage.afspraakLink && <a href={garage.afspraakLink} onClick={() => klik('afspraak')} data-testid="detail-afspraak" style={ctaBtn(c.accent)}><CalendarCheck size={16} /> {t('Afspraak')}</a>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 16 };
const specGrid = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' };
const hdrBtn = (bg, fg) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8, background: bg, color: fg || '#fff', fontWeight: 600, fontSize: 13, textDecoration: 'none', border: 'none', cursor: 'pointer' });
const ctaBtn = (bg) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7, padding: '11px', borderRadius: 9, background: bg, color: '#fff', fontWeight: 700, fontSize: 13.5, textDecoration: 'none' });
const pgBtn = (c, disabled) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '9px 14px', borderRadius: 8, border: `1px solid ${c.border2}`, background: c.surface, color: disabled ? c.textMuted : c.text, fontWeight: 600, fontSize: 13.5, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 });
const contactRow = (c) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: c.surface, borderRadius: 10, color: c.textSoft, textDecoration: 'none', fontSize: 14.5 });
const Spec = ({ c, l, v }) => <div><div style={{ fontSize: 11, color: c.textMuted }}>{l}</div><div style={{ fontWeight: 600, color: c.text }}>{v}</div></div>;
const Center = ({ children }) => <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#94a3b8', fontSize: 16 }}>{children}</div>;
const Empty = ({ c, children }) => <div style={{ textAlign: 'center', padding: '60px 20px', color: c.textMuted }}>{children}</div>;
